import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Save, X, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatError } from "../lib/api";

const EMPTY = { name: "", description: "", price: "", image: "", category: "experiencia", stock: 100 };

export default function GestionTienda() {
  const [products, setProducts] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // {id, name, description, price, image, category, stock}
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));

  useEffect(() => {
    load();
  }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const create = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/admin/products", {
        ...form,
        price: parseFloat(form.price) || 0,
        stock: parseInt(form.stock) || 0,
      });
      setForm(EMPTY);
      setShowForm(false);
    }, "Producto añadido");
  };

  const saveEdit = () =>
    run(async () => {
      await api.put(`/admin/products/${editing.id}`, {
        name: editing.name,
        description: editing.description,
        price: parseFloat(editing.price) || 0,
        image: editing.image,
        category: editing.category,
        stock: parseInt(editing.stock) || 0,
      });
      setEditing(null);
    }, "Producto actualizado");

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14" data-testid="gestion-tienda-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="overline-gold mb-2">Acceso restringido</p>
          <h1 className="text-3xl lg:text-4xl text-white font-light tracking-tight">
            Gestión de la <span className="text-gold italic">Tienda</span>
          </h1>
        </div>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); }} className="btn-gold-solid !px-6 !py-3" data-testid="toggle-add-product">
          <Plus size={16} /> {showForm ? "Cancelar" : "Añadir Producto"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card-dark p-6 mt-8 space-y-3" data-testid="add-product-form">
          <input className="input-dark" placeholder="Nombre del producto" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-name" />
          <textarea className="input-dark min-h-[70px]" placeholder="Descripción" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="add-description" />
          <div className="grid grid-cols-2 gap-3">
            <input className="input-dark" type="number" step="0.01" min="0" placeholder="Precio (€)" required value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="add-price" />
            <select className="input-dark" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="add-category">
              <option value="experiencia">Experiencia</option>
              <option value="servicio">Servicio</option>
              <option value="equipamiento">Equipamiento</option>
            </select>
          </div>
          <input className="input-dark" placeholder="URL de la imagen" value={form.image}
            onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="add-image" />
          <button type="submit" disabled={busy} className="btn-gold-solid w-full disabled:opacity-40" data-testid="add-submit">
            {busy ? "Guardando..." : "Guardar Producto"}
          </button>
        </form>
      )}

      <div className="mt-10 space-y-4">
        {products === null ? (
          <div className="py-16 flex justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>
        ) : products.length === 0 ? (
          <p className="py-16 text-white/40" data-testid="no-products-gestion">No hay productos. Añade el primero.</p>
        ) : (
          products.map((p) => (
            <div key={p.id} className="card-dark p-5" data-testid={`gestion-product-${p.id}`}>
              {editing?.id === p.id ? (
                <div className="space-y-3" data-testid={`edit-form-${p.id}`}>
                  <input className="input-dark" value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-name" />
                  <textarea className="input-dark min-h-[60px]" value={editing.description}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="edit-description" />
                  <div className="grid grid-cols-2 gap-3">
                    <input className="input-dark" type="number" step="0.01" min="0" value={editing.price}
                      onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="edit-price" />
                    <select className="input-dark" value={editing.category}
                      onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="edit-category">
                      <option value="experiencia">Experiencia</option>
                      <option value="servicio">Servicio</option>
                      <option value="equipamiento">Equipamiento</option>
                    </select>
                  </div>
                  <input className="input-dark" placeholder="URL de la imagen" value={editing.image}
                    onChange={(e) => setEditing({ ...editing, image: e.target.value })} data-testid="edit-image" />
                  <div className="flex gap-3">
                    <button onClick={saveEdit} disabled={busy} className="btn-gold-solid flex-1 disabled:opacity-40" data-testid="edit-save">
                      <Save size={15} /> {busy ? "Guardando..." : "Guardar Cambios"}
                    </button>
                    <button onClick={() => setEditing(null)} className="btn-ghost-light" data-testid="edit-cancel">
                      <X size={15} /> Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-16 h-16 object-cover shrink-0" />
                  ) : (
                    <div className="w-16 h-16 bg-forest flex items-center justify-center shrink-0">
                      <ShoppingBag size={20} className="text-gold/50" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{p.name}</p>
                    <p className="text-sand text-sm mt-0.5">{eur(p.price)} · <span className="text-white/40 capitalize">{p.category}</span></p>
                  </div>
                  <button onClick={() => { setEditing({ ...p }); setShowForm(false); }}
                    className="text-white/50 hover:text-gold transition-colors p-2" data-testid={`edit-btn-${p.id}`} title="Editar">
                    <Pencil size={18} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => run(() => api.delete(`/admin/products/${p.id}`), "Producto eliminado")} disabled={busy}
                    className="text-white/40 hover:text-red-400 transition-colors p-2" data-testid={`delete-btn-${p.id}`} title="Eliminar">
                    <Trash2 size={18} strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <p className="mt-10 text-center">
        <Link to="/admin" className="text-white/30 hover:text-gold text-xs uppercase tracking-[0.15em] transition-colors" data-testid="back-admin">
          ← Ir al panel completo
        </Link>
      </p>
    </div>
  );
}
